import React, { useEffect, useMemo, useState } from 'react';
import { POST_CREATE_STRIPE_PAYMENT_INTENT, POST_VERIFY_STRIPE_PAYMENT_INTENT } from 'services/api';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { StripeCardForm } from './StripeCardForm';
import { FIELD_NAMES } from '../BillingAddress/BillingAddress.schema';
import {
  checkoutStripePublishableApiKeySelector,
} from '../../../../redux/slices/checkout';
import { setStripeData } from 'redux/slices/cart';

export const StripeFormContainer = ({
  watch,
  onError,
  onSuccess,
  show,
  isLoading,
  setIsLoading
}) => {
  const { id } = useParams();
  const [stripeClientSecret, setStripeSecret] = useState(
    new URLSearchParams(window.location.search).get("payment_intent_client_secret")
  );
  const dispatch = useDispatch();

  useEffect(() => {
    if (!show) {
      return;
    }

    if (stripeClientSecret) {
      onSuccess();
    }
  }, [onSuccess, stripeClientSecret, show]);

  const {
    isAnonymous,
    userPaysFees,
    entities,
    comments
  } = useSelector(state => state.cart);

  const {
    phone_country,
    phone
  } = useSelector(state => state.checkoutForm.shopperDetails);
  const phoneNumber = phone_country.dial_code + phone;
  const stripePublishableApiKey = useSelector(checkoutStripePublishableApiKeySelector);

  const products = Object.values(entities).map(prod => ({
    ...prod.product,
    quantity: prod.quantity,
    type: prod.type
  }));

  const stripePromise = useMemo(() => {
    if (!stripePublishableApiKey) {
      return;
    };

    return loadStripe(stripePublishableApiKey);
  }, [stripePublishableApiKey])

  const shippingDetailsValues = watch();

  const handleSuccess = (paymentIntent) => {
    POST_VERIFY_STRIPE_PAYMENT_INTENT(id, paymentIntent.id).then(payload => {
      window.location.replace(`/order/${payload.data.id}/success?token=${payload.data.view_token}`)
    });
  }

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (stripeClientSecret) {
      stripePromise.then(async stripe => {
        const { paymentIntent } = await stripe.retrievePaymentIntent(stripeClientSecret);

        let error = '';

        switch (paymentIntent?.status) {
          case "succeeded":
          case "requires_capture":
            handleSuccess(paymentIntent);
            return;
          case "processing":
            error = 'Your payment is processing.';
            break;
          case "requires_payment_method":
            error = 'Your payment was not successful, please try again.';
            break;
          default:
            error = 'Something went wrong.';
            break;
        };

        onError(error);
      });
      return;
    }

    if (!show) {
      return;
    }

    const getSecret = async () => {
      const isBillingSameAsShipping = shippingDetailsValues[FIELD_NAMES.billingAddressSameAsShipping];
      const shippingCountryCode = shippingDetailsValues?.[FIELD_NAMES.country]?.code;
      const billingCountryCode = shippingDetailsValues?.[isBillingSameAsShipping ? FIELD_NAMES.country : FIELD_NAMES.billing_country]?.code;

      setIsLoading(true);
      try {
        const { meta, data, status_code, ...apiError } = await POST_CREATE_STRIPE_PAYMENT_INTENT(
          id,
          products,
          {
            recipient_name: shippingDetailsValues[FIELD_NAMES.name],
            phone_number: phoneNumber,
            address_1: shippingDetailsValues[FIELD_NAMES.address_1],
            address_2: shippingDetailsValues[FIELD_NAMES.address_2],
            country: shippingCountryCode,
            city: shippingDetailsValues[FIELD_NAMES.city],
            state: shippingDetailsValues[FIELD_NAMES.state],
            postcode: shippingDetailsValues[FIELD_NAMES.postcode],
          },
          comments,
          isAnonymous,
          {
            name: shippingDetailsValues[isBillingSameAsShipping ? FIELD_NAMES.name : FIELD_NAMES.billing_name],
            phone_number: phoneNumber,
            address_1: shippingDetailsValues[isBillingSameAsShipping ? FIELD_NAMES.address_1 : FIELD_NAMES.billing_address_1],
            address_2: shippingDetailsValues[isBillingSameAsShipping ? FIELD_NAMES.address_2 : FIELD_NAMES.billing_address_2],
            country: billingCountryCode,
            city: shippingDetailsValues[isBillingSameAsShipping ? FIELD_NAMES.city : FIELD_NAMES.billing_city],
            postcode: shippingDetailsValues[isBillingSameAsShipping ? FIELD_NAMES.postcode : FIELD_NAMES.billing_postcode],
            state: shippingDetailsValues[isBillingSameAsShipping ? FIELD_NAMES.state : FIELD_NAMES.billing_state]
          },
          shippingDetailsValues[FIELD_NAMES.shippingMethod],
          userPaysFees
        );

        if (status_code) {
          throw apiError;
        }

        const clientSecret = meta.payment_intent_client_secret;

        setStripeSecret(clientSecret);
        dispatch(setStripeData(data));
      } catch (error) {
        onError(error);
      } finally {
        setIsLoading(false);
      }
    };

    getSecret();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripeClientSecret, show]);

  const options = useMemo(() => {
    if (!show) {
      return;
    }

    return {
      clientSecret: stripeClientSecret
    }
  }, [show, stripeClientSecret]);

  return (
    <>
      {options?.clientSecret && stripeClientSecret && stripePromise && (
        <Elements stripe={stripePromise} options={options} >
          <StripeCardForm
            clientSecret={stripeClientSecret}
            onSuccess={handleSuccess}
          />
        </Elements>
      )}
    </>
  )
}

StripeFormContainer.propTypes = {
  watch: PropTypes.func,
  onError: PropTypes.func,
  onSuccess: PropTypes.func,
  show: PropTypes.bool,
  isLoading: PropTypes.bool,
  setIsLoading: PropTypes.func
}
